import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { User } from '../services/types';
import keycloak from '../keycloak';
import { clearStoredActiveAppUser, getStoredActiveAppUser } from '../services/activeAppUser';
import {
  AUTH_STORAGE_EVENT,
  getStorage,
  isJustLoggedOut,
  removeStorage,
  setStorage,
} from '../utils/authStorage';
import { clearSessionTimeout, hasActiveAuthSession } from '../utils/authSession';
import { clearTimeoutRedirectPath, SESSION_TIMEOUT_EVENT } from '../utils/sessionTimeout';
import { DEFAULT_POST_AUTH_PATH } from '../rbac/landingPage';

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  signIn: (redirectTo?: string, opts?: { promptLogin?: boolean }) => Promise<void>;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthContextValue | undefined>(undefined);

function readStoredUser(): User | null {
  if (!hasActiveAuthSession()) return null;
  return getStorage<User>('kcUser');
}

function usersEqual(a: User | null, b: User | null): boolean {
  if (a === b) return true;
  if (!a || !b) return !a && !b;
  return a.id === b.id && a.displayName === b.displayName && a.role === b.role;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => readStoredUser());
  const [loading, setLoading] = useState(false);

  const signIn = useCallback(async (redirectTo?: string, opts?: { promptLogin?: boolean }) => {
    setLoading(true);
    try {
      // User is explicitly signing in again (e.g. after timeout); allow App init to proceed after redirect.
      clearSessionTimeout();
      removeStorage('token');
      const path = (redirectTo ?? DEFAULT_POST_AUTH_PATH).startsWith('/')
        ? (redirectTo ?? DEFAULT_POST_AUTH_PATH)
        : `/${redirectTo ?? DEFAULT_POST_AUTH_PATH.slice(1)}`;
      const forceLogin = isJustLoggedOut() || opts?.promptLogin === true;
      await keycloak.login({
        redirectUri: window.location.origin + path,
        ...(forceLogin ? { prompt: 'login', maxAge: 0 } : null),
      });
    } finally {
      setLoading(false);
    }
  }, []);
  const signOut = useCallback(async () => {
    console.info('[auth] signOut: start', { href: window.location.href });
    // Clear app auth state first (in-memory + storage) so we don't "restore" a session on reload.
    setUser(null);
    try {
      sessionStorage.clear();
    } catch {
      // ignore
    }

    // Set before clearing tokens so new tabs cannot miss the flag during logout.
    setStorage('justLoggedOut', true);
    clearTimeoutRedirectPath();
    removeStorage('token');
    removeStorage('kcUser');
    clearStoredActiveAppUser();
    removeStorage('timeOut');

    // Perform a real Keycloak logout and land on a public route; that route immediately redirects to Keycloak login.
    const redirectUri = window.location.origin + '/logged-out';
    console.info('[auth] signOut: calling keycloak.logout', { redirectUri });
    try {
      await keycloak.logout({ redirectUri });
      console.info('[auth] signOut: keycloak.logout resolved');
    } catch (e) {
      console.error('[auth] signOut: keycloak.logout failed', e);
      throw e;
    }
  }, []);
  // Best-effort: if App.tsx stored token/user, reflect it.
  const syncFromStorage = useCallback(() => {
    if (!hasActiveAuthSession()) {
      setUser((prev) => (prev === null ? prev : null));
      return;
    }
    const u = getStorage<User>('kcUser');
    const app = getStoredActiveAppUser();
    const next: User | null =
      u && app
        ? {
            ...u,
            id: app.id.trim() || u.id,
            displayName: app.displayName || u.displayName,
            role: u.role,
          }
        : (u ?? null);
    setUser((prev) => (usersEqual(prev, next) ? prev : next));
  }, []);

  useEffect(() => {
    // cross-tab sync (e.g. logout/timeout in another tab)
    const handler = (e: StorageEvent) => {
      if (!e.key?.startsWith('agd.')) {
        syncFromStorage();
        return;
      }

      if (isJustLoggedOut() && keycloak.authenticated) {
        console.info('[auth] cross-tab logout detected, forcing interactive login');
        setUser(null);
        void keycloak.login({
          redirectUri: window.location.origin + '/logged-out',
          prompt: 'login',
          maxAge: 0,
        });
        return;
      }

      syncFromStorage();
    };
    window.addEventListener('storage', handler);
    const localHandler = (e: Event) => {
      const key = (e as CustomEvent<{ key?: string }>).detail?.key;
      // Token refresh writes on every API call; ignore so pages don't re-fetch in a loop.
      if (key === 'token' || key === 'timeOut') return;
      syncFromStorage();
    };
    window.addEventListener(AUTH_STORAGE_EVENT, localHandler as EventListener);
    const timeoutHandler = () => setUser(null);
    window.addEventListener(SESSION_TIMEOUT_EVENT, timeoutHandler);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener(AUTH_STORAGE_EVENT, localHandler as EventListener);
      window.removeEventListener(SESSION_TIMEOUT_EVENT, timeoutHandler);
    };
  }, [syncFromStorage]);

  const value = useMemo(
    () => ({
      user,
      loading,
      signIn,
      signOut,
    }),
    [user, loading, signIn, signOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used within AuthProvider');
  return v;
}
